import React, { useEffect, Component } from 'react';
import { X, AlertTriangle, Package, MapPin, CheckCircle } from 'lucide-react';
import { Product, WA_NUMBER } from '../data/products';
import { WhatsAppIcon } from './icons/WhatsAppIcon';
interface ProductModalProps {
  product: Product | null;
  onClose: () => void;
}
export function ProductModal({
  product,
  onClose
}: ProductModalProps) {
  // Lock body scroll when open
  useEffect(() => {
    if (product) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [product]);
  // Close on ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  if (!product) return null;
  const waMsg = encodeURIComponent(`Hola! Quiero el ${product.name} con 20% OFF — precio $${product.currentPrice} UY. ¿Tienen stock disponible?`);
  const waLink = `https://wa.me/${WA_NUMBER}?text=${waMsg}`;
  return <div className="fixed inset-0 bg-black/90 z-[9999] overflow-y-auto p-[30px_20px] backdrop-blur-sm flex items-start justify-center" onClick={e => {
    if (e.target === e.currentTarget) onClose();
  }} data-id="element-275">
      <div className="bg-dark-600 border border-[#333] rounded-xl max-w-[780px] w-full overflow-hidden relative m-auto animate-modal-in" data-id="element-276">
        <button onClick={onClose} className="absolute top-[15px] right-[20px] bg-transparent border-none text-white text-[2rem] cursor-pointer z-20 leading-none transition-colors duration-200 hover:text-alphaGold" aria-label="Close modal" data-id="element-277">
          <X size={32} data-id="element-278" />
        </button>

        <div className="w-full h-[320px] bg-white flex items-center justify-center overflow-hidden" data-id="element-279">
          <img src={product.image} alt={product.name} className="w-full h-full object-contain p-[30px]" onError={e => {
          e.currentTarget.src = product.fallbackImage;
        }} data-id="element-280" />
        </div>

        <div className="p-[35px]" data-id="element-281">
          <div className="inline-block bg-alphaGold text-white text-[0.75rem] font-extrabold py-1 px-3.5 rounded-sm tracking-[2px] uppercase mb-[15px]" data-id="element-282">
            20% OFF — OFERTA LIMITADA
          </div>

          <h2 className="text-[1.6rem] font-black uppercase tracking-[2px] text-white mb-[15px]" data-id="element-283">
            {product.name}
          </h2>

          <div className="bg-alphaGold/10 border-l-4 border-l-alphaGold p-[18px_20px] mb-5 rounded-r" data-id="element-284">
            <h4 className="text-alphaGold text-[0.85rem] uppercase tracking-[2px] mb-2 font-bold flex items-center gap-2" data-id="element-285">
              <AlertTriangle className="w-4 h-4" data-id="element-286" /> El Problema que Resuelve
            </h4>
            <p className="text-[#ccc] text-[0.92rem] leading-[1.7]" data-id="element-287">
              {product.pain}
            </p>
          </div>

          <p className="text-[#aaa] leading-[1.8] text-[0.97rem] mb-[25px]" data-id="element-288">
            {product.description}
          </p>

          <p className="text-[0.85rem] uppercase tracking-[2px] text-[#666] mb-3 font-bold flex items-center gap-2" data-id="element-289">
            <CheckCircle className="w-4 h-4 text-alphaGold" data-id="element-290" /> BENEFICIOS CLAVE
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-[25px]" data-id="element-291">
            {product.benefits.map((benefit, idx) => <div key={idx} className="flex items-start gap-2.5 bg-[#1e1e1e] p-3 rounded" data-id="element-292">
                <span className="text-alphaGold font-black text-[1rem] flex-shrink-0 mt-px" data-id="element-293">
                  ✓
                </span>
                <span className="text-[#ccc] text-[0.85rem] leading-[1.5]" data-id="element-294">
                  {benefit}
                </span>
              </div>)}
          </div>

          <div className="flex items-center gap-5 mb-[25px] flex-wrap" data-id="element-295">
            <span className="text-[1rem] text-[#555] line-through" data-id="element-296">
              ${product.originalPrice.toLocaleString('es-UY')}
            </span>
            <span className="text-[2.5rem] font-black text-alphaGold" data-id="element-297">
              <span className="text-[1.2rem] align-top mt-2 inline-block mr-1" data-id="element-298">
                $
              </span>
              {product.currentPrice.toLocaleString('es-UY')}
            </span>
            <span className="bg-alphaGold/15 border border-alphaGold/40 text-alphaGold text-[0.85rem] font-extrabold py-1.5 px-4 rounded-full" data-id="element-299">
              AHORRÁS 20%
            </span>
          </div>

          <div className="bg-alphaGold/10 border border-alphaGold/25 rounded p-[14px_18px] mb-5 text-[0.88rem] text-alphaGold font-semibold text-center flex items-center justify-center gap-2" data-id="element-300">
            <AlertTriangle className="w-4 h-4" data-id="element-301" /> Stock limitado · Esta oferta
            puede terminar en cualquier momento
          </div>

          <div className="bg-dark-500 border border-dark-300 rounded p-[15px_18px] mb-5 text-[0.85rem] text-[#999] space-y-2" data-id="element-302">
            <div className="flex items-start gap-2" data-id="element-303">
              <Package className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-304" />
              <span data-id="element-305">
                <strong className="text-white" data-id="element-306">Envío a domicilio:</strong> desde
                $190 pesos uruguayos, varía según tu zona
              </span>
            </div>
            <div className="flex items-start gap-2" data-id="element-307">
              <MapPin className="w-4 h-4 mt-0.5 text-alphaGold flex-shrink-0" data-id="element-308" />
              <span data-id="element-309">
                <strong className="text-white" data-id="element-310">Pick Up:</strong> Malvín,
                Montevideo
              </span>
            </div>
          </div>

          <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3 bg-whatsapp text-white text-[1.05rem] font-extrabold py-[18px] px-[30px] rounded uppercase tracking-[1px] transition-all duration-300 w-full shadow-[0_6px_25px_rgba(37,211,102,0.3)] hover:bg-whatsappDark hover:scale-[1.02]" data-id="element-311">
            <WhatsAppIcon className="w-6 h-6" data-id="element-312" />
            Quiero Este Producto — Pedir por WhatsApp
          </a>
        </div>
      </div>
    </div>;
}